import React from 'react'
import { Bell, Loader2, Mail } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/components/ui/use-toast'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import {
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferencesMutation,
} from '@/redux/marketerApi'
import { SettingsCard } from './SettingsCard'

/** Settings > Notifications: each kind of update, by email and as a browser notification */
export const NotificationsTab: React.FC = () => {
  const { toast } = useToast()
  const { data, isLoading } = useGetNotificationPreferencesQuery()
  const [save] = useSetNotificationPreferencesMutation()
  const push = usePushNotifications()

  const set = async (id: string, channel: 'email' | 'push', value: boolean) => {
    try {
      await save({ [id]: { [channel]: value } }).unwrap()
    } catch {
      toast({ title: "Couldn't save that", variant: 'destructive' })
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      {push.permissionState !== 'granted' ? (
        <section className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#BFD7FB] bg-[#EFF5FF] p-4">
          <Bell className="h-5 w-5 shrink-0 text-[#2563EB]" />
          <p className="min-w-0 flex-1 text-sm text-foreground">
            {push.permissionState === 'denied'
              ? 'Browser notifications are blocked for Athios. Allow them in your browser’s site settings to get them here.'
              : push.permissionState === 'unsupported'
                ? "This browser doesn't support notifications. Try Chrome, Edge or Safari, or install the app."
                : 'Turn on browser notifications on this device to get the ones ticked below.'}
          </p>
          {push.permissionState === 'default' ? (
            <button
              type="button"
              onClick={async () => {
                const ok = await push.subscribe()
                toast({
                  title: ok ? 'Browser notifications on' : 'Notifications were not turned on',
                })
              }}
              disabled={push.isSubscribing}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0F172A] px-4 text-sm font-semibold text-[#FFFFFF]"
            >
              {push.isSubscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Turn on
            </button>
          ) : null}
        </section>
      ) : null}

      <SettingsCard
        title="Notification preferences"
        subtitle="Choose what you want to be notified about. You can always change these later."
      >
        {isLoading || !data ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="text-left">
                  <th className="pb-3 font-medium text-muted-foreground" />
                  <th className="w-36 pb-3 text-center">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                      <Mail className="h-4 w-4" /> Email
                    </span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      Updates by email
                    </span>
                  </th>
                  <th className="w-36 pb-3 text-center">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                      <Bell className="h-4 w-4" /> Browser
                    </span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      Alerts on this device
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.categories.map((c) => (
                  <tr key={c.id}>
                    <td className="py-3 text-foreground">{c.label}</td>
                    <td className="py-3 text-center">
                      <Checkbox
                        checked={c.email}
                        onCheckedChange={(v) => set(c.id, 'email', v === true)}
                        aria-label={`${c.label} by email`}
                        className="data-[state=checked]:border-[#16A34A] data-[state=checked]:bg-[#16A34A]"
                      />
                    </td>
                    <td className="py-3 text-center">
                      <Checkbox
                        checked={c.push}
                        onCheckedChange={(v) => set(c.id, 'push', v === true)}
                        aria-label={`${c.label} in the browser`}
                        className="data-[state=checked]:border-[#16A34A] data-[state=checked]:bg-[#16A34A]"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Updates always appear under the bell in Athios. Bills and security emails are always
          sent.
        </p>
      </SettingsCard>
    </div>
  )
}

export default NotificationsTab
